/**
 * Shared Explorer Prospector job hooks.
 * Company cards, person profiles, and update sections all use this same job
 * lifecycle and socket hydration behavior.
 */

const {
  useState: useExplorerProspectorState,
  useEffect: useExplorerProspectorEffect,
  useCallback: useExplorerProspectorCallback,
} = React;

const ExplorerProspectorConfig = window.ExplorerConfig || {};
const EXPLORER_PROSPECTOR_JOB_UPDATE_EVENT = 'explorer:prospector-job-update';
const EXPLORER_PROSPECTOR_ACTIVE_STATUSES = new Set(['pending', 'running']);

function calculateExplorerProspectorProgress(elapsedMs, totalMs = 30000) {
  if (typeof window.calculateProgress === 'function') {
    return window.calculateProgress(elapsedMs, totalMs);
  }
  const normalizedTime = elapsedMs / totalMs;
  return Math.min(100, Math.round(100 * (1 - Math.exp(-3 * normalizedTime))));
}

function useExplorerProspectorSocket(productId) {
  useExplorerProspectorEffect(() => {
    if (!ExplorerProspectorConfig.IS_DASHBOARD || !productId || typeof window.io !== 'function') {
      return undefined;
    }

    const socket = window.io({
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      forceNew: true,
    });

    const subscribe = () => {
      socket.emit('prospector.subscribe', { product_id: productId });
    };

    socket.on('connect', subscribe);
    socket.on('reconnect', subscribe);
    socket.on('server.auth_failed', (data) => {
      console.error('Explorer Prospector socket auth_failed', data);
    });
    socket.on('server.prospector_job_update', (evt) => {
      window.dispatchEvent(new CustomEvent(EXPLORER_PROSPECTOR_JOB_UPDATE_EVENT, { detail: evt }));
    });

    return () => {
      socket.off('connect', subscribe);
      socket.off('reconnect', subscribe);
      socket.off('server.prospector_job_update');
      socket.off('server.auth_failed');
      socket.disconnect();
    };
  }, [productId]);
}

function useExplorerSmoothProspectorProgress(active, completed, totalMs = 30000) {
  const [progress, setProgress] = useExplorerProspectorState(0);
  const [elapsedSeconds, setElapsedSeconds] = useExplorerProspectorState(0);

  useExplorerProspectorEffect(() => {
    if (completed) {
      setProgress(100);
      return undefined;
    }
    if (!active) {
      setProgress(0);
      setElapsedSeconds(0);
      return undefined;
    }

    const startedAt = Date.now();
    const tick = () => {
      const elapsedMs = Date.now() - startedAt;
      setElapsedSeconds(Math.floor(elapsedMs / 1000));
      setProgress(Math.min(95, calculateExplorerProspectorProgress(elapsedMs, totalMs)));
    };
    tick();
    const timer = setInterval(tick, 500);
    return () => clearInterval(timer);
  }, [active, completed, totalMs]);

  return { progress, elapsedSeconds };
}

function useExplorerProfileProspectorJob(initialState) {
  const [jobState, setJobState] = useExplorerProspectorState(null);
  const [queueing, setQueueing] = useExplorerProspectorState(false);
  const [error, setError] = useExplorerProspectorState(null);

  const updateFromPayload = useExplorerProspectorCallback((payload) => {
    if (!payload) return;
    setJobState((prev) => {
      const existingJob = prev?.job || {};
      const nextJob = payload.job === null
        ? existingJob
        : {
            ...existingJob,
            ...(payload.job || {}),
            ...(payload.status ? { status: payload.status } : {}),
            ...(payload.error ? { error: payload.error } : {}),
          };
      return {
        ...(prev || {}),
        sheet: payload.sheet || prev?.sheet || null,
        redirect_url: payload.redirect_url || prev?.redirect_url || null,
        source_sheet: payload.source_sheet || prev?.source_sheet || null,
        mode: payload.mode || prev?.mode || null,
        job: Object.keys(nextJob).length > 0 ? nextJob : null,
      };
    });
  }, []);

  useExplorerProspectorEffect(() => {
    if (initialState === undefined) return;
    setQueueing(false);
    setError(null);
    if (initialState) {
      updateFromPayload(initialState);
    } else {
      setJobState(null);
    }
  }, [initialState, updateFromPayload]);

  const start = useExplorerProspectorCallback(async (queueRequest, { storageKey, always } = {}) => {
    if (queueing) return null;
    if (always && storageKey) localStorage.setItem(storageKey, 'true');
    setQueueing(true);
    setError(null);
    setJobState(null);
    try {
      const data = await queueRequest();
      if (!data.success) throw new Error(data.error || 'Failed to queue Prospector job.');
      updateFromPayload(data);
      return data;
    } catch (e) {
      setError(e.message || 'Failed to queue Prospector job.');
      return null;
    } finally {
      setQueueing(false);
    }
  }, [queueing, updateFromPayload]);

  const sheetId = jobState?.sheet?.id || null;
  const jobId = jobState?.job?.id || null;
  const status = jobState?.job?.status || (jobState?.sheet?.progress ? 'running' : null);
  const active = !!jobState && EXPLORER_PROSPECTOR_ACTIVE_STATUSES.has(status);
  const completed = !!jobState && status === 'completed';
  const failed = !!jobState && (status === 'failed' || status === 'aborted');
  const rows = Array.isArray(jobState?.sheet?.rows) ? jobState.sheet.rows : [];
  const jobError = error || jobState?.job?.error || null;

  const refreshSheet = useExplorerProspectorCallback(() => {
    if (!sheetId) return Promise.resolve(null);
    return window.ExplorerApi.getProspectorSheet(sheetId)
      .then(data => {
        if (data.success) {
          updateFromPayload(data);
          return data;
        }
        setError(data.error || 'Failed to refresh Prospector sheet.');
        return data;
      })
      .catch((e) => {
        setError(e.message || 'Failed to refresh Prospector sheet.');
        return null;
      });
  }, [sheetId, updateFromPayload]);

  useExplorerProspectorEffect(() => {
    if (!jobId && !sheetId) return undefined;
    const handler = (event) => {
      const evt = event.detail || {};
      if ((jobId && evt.job_id === jobId) || (sheetId && evt.sheet_id === sheetId)) {
        updateFromPayload({
          status: evt.status,
          error: evt.error,
          job: {
            id: evt.job_id || jobId,
            kind: evt.kind,
            status: evt.status,
            error: evt.error,
          },
        });
        refreshSheet();
      }
    };
    window.addEventListener(EXPLORER_PROSPECTOR_JOB_UPDATE_EVENT, handler);
    return () => window.removeEventListener(EXPLORER_PROSPECTOR_JOB_UPDATE_EVENT, handler);
  }, [jobId, sheetId, refreshSheet, updateFromPayload]);

  useExplorerProspectorEffect(() => {
    if (!active || !sheetId) return undefined;
    const timer = setInterval(refreshSheet, 2500);
    return () => clearInterval(timer);
  }, [active, sheetId, refreshSheet]);

  const progressState = useExplorerSmoothProspectorProgress(active || queueing, completed, 30000);

  return {
    jobState,
    queueing,
    error: jobError,
    active,
    completed,
    failed,
    rows,
    start,
    hydrate: updateFromPayload,
    refreshSheet,
    progress: progressState.progress,
    elapsedSeconds: progressState.elapsedSeconds,
  };
}

window.ExplorerProspector = {
  PROSPECTOR_JOB_UPDATE_EVENT: EXPLORER_PROSPECTOR_JOB_UPDATE_EVENT,
  calculateSmoothProspectorProgress: calculateExplorerProspectorProgress,
  useExplorerProspectorSocket,
  useSmoothProspectorProgress: useExplorerSmoothProspectorProgress,
  useProfileProspectorJob: useExplorerProfileProspectorJob,
};
